import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  PackageCheck,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IsoWeek } from '@obraplan/core/domain/date/iso-week';
import { DEFAULT_PROJECT_TIMEZONE } from '@obraplan/core/domain/calendar/constants';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import {
  entryIdsOf,
  OVERDUE_LOOKBACK_WEEKS,
  SECTION_LABEL,
  type SupplyGroup,
  type SupplySection,
} from '@obraplan/core/domain/supply/supply-digest';
import { describeDeadline } from '@obraplan/core/domain/schedule/material-alert';
import type { ActivityId } from '@obraplan/core/domain/shared/ids';
import type { SupplyDigestView } from '@obraplan/core/application/use-cases/schedule/supply-use-cases';
import type { RequisitionHistoryView } from '@obraplan/core/application/use-cases/schedule/requisition-use-cases';
import type { MaterialRequisition } from '@obraplan/core/domain/supply/requisition';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState, LoadingState } from '@/components/feedback/states';
import { formatBusinessDate, formatDateTime } from '@/lib/format';
import {
  useContainer,
  usePermission,
  useSession,
  useSessionState,
} from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Suprimentos — o aviso, não a compra. O ciclo de compra vive no sistema do cliente; aqui a
 * tela só diz o que o cronograma exige na obra e registra que alguém viu. Ciência não é
 * recebimento, e a tela diz isso em voz alta.
 */
/** Quantidade com a precisão da unidade (RN-020) — a tela recebe só código e precisão. */
function quantity(value: number, decimals: number, unitCode: string): string {
  const text = value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${text} ${unitCode}`;
}

export function SuppliesPage() {
  const container = useContainer();
  const session = useSession();
  const { activeProjectId } = useSessionState();
  const canAcknowledge = usePermission(PERMISSION.REQUIREMENTS_STATUS_CHANGE);
  const [justAcknowledged, setJustAcknowledged] = useState<string | undefined>(undefined);
  const [week, setWeek] = useState(() =>
    IsoWeek.of(container.clock.today(DEFAULT_PROJECT_TIMEZONE)),
  );

  const result = useLive(
    () =>
      activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getSupplyDigest.execute(
            { projectId: activeProjectId, isoYear: week.isoYear, isoWeek: week.isoWeek },
            session,
          ),
    [activeProjectId, session, week.key()],
  );

  // O registro do que já foi requisitado: sem ele, dar ciência some com o item e não deixa recibo.
  const historico = useLive(
    () =>
      activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listProjectRequisitions.execute(activeProjectId, session),
    [activeProjectId, session],
  );

  const [acknowledge] = useCommand(
    (entryIds: readonly string[]) =>
      container.acknowledgeSupplyMaterial.execute(
        { projectId: activeProjectId, entryIds: [...entryIds] },
        session,
      ),
    {
      successMessage: (summary) =>
        summary.acknowledged === 1
          ? 'Ciência registrada.'
          : `Ciência registrada em ${String(summary.acknowledged)} frentes.`,
      onSuccess: () => setJustAcknowledged(undefined),
    },
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        icon={<PackageCheck aria-hidden="true" />}
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo."
      />
    );
  }

  if (result === undefined) return <LoadingState label="Lendo o cronograma…" />;
  if (!result.ok) {
    return (
      <EmptyState
        icon={<AlertTriangle aria-hidden="true" />}
        title="Não foi possível montar a lista"
        description={result.error.message}
      />
    );
  }

  const view = result.value;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Suprimentos</h1>
          <p className="text-(--color-content-muted)">
            O que o cronograma exige que esteja na obra.
          </p>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Semana anterior"
            onClick={() => setWeek(week.previous())}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <span className="min-w-40 text-center font-medium tabular">
            Semana {view.digest.isoWeek} de {view.digest.isoYear}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Próxima semana"
            onClick={() => setWeek(week.next())}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setWeek(IsoWeek.of(container.clock.today(DEFAULT_PROJECT_TIMEZONE)))}
          >
            Semana atual
          </Button>
        </div>
      </header>

      {/* Quem clica precisa saber que está requisitando, e não só limpando a lista. */}
      <Card className="border-(--color-state-info)">
        <CardContent className="flex gap-3 p-4 text-sm">
          <ClipboardCheck
            className="mt-0.5 size-5 shrink-0 text-(--color-state-info)"
            aria-hidden="true"
          />
          <div>
            <p className="font-medium">Dar ciência emite uma requisição.</p>
            <p className="text-(--color-content-muted)">
              Material com código provisório do orçamento (ORC-…) não recebe ciência: sem o SKU de
              compras a requisição não sai. Vincule-o ao SKU no{' '}
              <Link to="/configuracoes/materiais" className="underline">
                catálogo de materiais
              </Link>{' '}
              e ele volta a poder ser requisitado.
            </p>
          </div>
        </CardContent>
      </Card>

      {view.digest.pendingMaterials === 0 ? (
        <EmptyState
          icon={<Check aria-hidden="true" />}
          title="Nada aguardando ciência"
          description="Nada com data nesta semana, na próxima, nem pendente das anteriores."
        />
      ) : (
        view.digest.sections.map((section) => (
          <SupplySectionCard
            key={section.section}
            section={section.section}
            groups={section.groups}
            view={view}
            canAcknowledge={canAcknowledge}
            pending={justAcknowledged}
            onAcknowledge={(group) => {
              setJustAcknowledged(group.materialId);
              void acknowledge(entryIdsOf(group));
            }}
          />
        ))
      )}

      {historico?.ok === true && historico.value.requisitions.length > 0 && (
        <RequisitionHistory view={historico.value} />
      )}
    </div>
  );
}

function SupplySectionCard({
  section,
  groups,
  view,
  canAcknowledge,
  pending,
  onAcknowledge,
}: {
  section: SupplySection;
  groups: readonly SupplyGroup[];
  view: SupplyDigestView;
  canAcknowledge: boolean;
  pending: string | undefined;
  onAcknowledge: (group: SupplyGroup) => void;
}) {
  if (groups.length === 0) return null;

  const materialById = new Map(view.materials.map((material) => [material.materialId, material]));
  const activityById = new Map(view.activities.map((activity) => [activity.activityId, activity]));

  // Com mais de uma torre, o nome dela entra no rótulo: "Estrutural — Térreo" apareceria
  // repetido e sem como distinguir, e a lista existe para dizer onde o material vai.
  const manyTowers = new Set(view.activities.map((activity) => activity.towerName)).size > 1;
  const placeOf = (activityId: ActivityId): string => {
    const activity = activityById.get(activityId);
    if (activity === undefined) return 'frente não encontrada';
    return manyTowers ? `${activity.towerName} · ${activity.label}` : activity.label;
  };

  return (
    <Card className={section === 'UNACKNOWLEDGED' ? 'border-(--color-state-risk)' : undefined}>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {SECTION_LABEL[section]}
          <Badge tone={section === 'UNACKNOWLEDGED' ? 'risk' : 'info'}>
            {groups.length} {groups.length === 1 ? 'material' : 'materiais'}
          </Badge>
        </CardTitle>
        {section === 'UNACKNOWLEDGED' && (
          <CardDescription>
            Últimas {OVERDUE_LOOKBACK_WEEKS} semanas. Mais antigo não aparece aqui.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {groups.map((group) => {
          const material = materialById.get(group.materialId);
          return (
            <article
              key={group.materialId}
              className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">
                    {material?.name ?? group.materialId}
                    {material !== undefined && (
                      <span className="ml-2 text-sm font-normal text-(--color-content-muted)">
                        {material.code}
                      </span>
                    )}
                    {material?.codeIsProvisional === true && (
                      <Badge tone="warn" className="ml-2">
                        Código provisório
                      </Badge>
                    )}
                  </p>
                  {/* O total primeiro: quem compra pensa em quantidade, não em linha. */}
                  <p className="text-lg tabular">
                    {quantity(
                      group.totalQuantity,
                      material?.decimals ?? 2,
                      material?.unitCode ?? '',
                    )}
                  </p>
                </div>
                {/* RN-347 — sem SKU não há requisição, e a ciência sem requisição some com a
                    linha sem recibo. O caminho é o vínculo, e a tela diz qual. */}
                {canAcknowledge && material?.codeIsProvisional === true && (
                  <p className="max-w-56 text-xs text-(--color-state-warn)">
                    Sem SKU de compras. Vincule-o no{' '}
                    <Link to="/configuracoes/materiais" className="underline">
                      catálogo de materiais
                    </Link>{' '}
                    para dar ciência.
                  </p>
                )}
                {canAcknowledge && material?.codeIsProvisional !== true && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={pending === group.materialId}
                    onClick={() => onAcknowledge(group)}
                  >
                    <Check aria-hidden="true" />
                    {pending === group.materialId ? 'Registrando…' : 'Dar ciência'}
                  </Button>
                )}
              </div>

              {/* Onde o material vai — a pergunta seguinte de quem compra, sempre. */}
              <ul className="flex flex-col gap-1 text-sm text-(--color-content-muted)">
                {group.lines.map((line) => (
                  <li key={line.entryId} className="flex flex-wrap gap-x-2">
                    <span className="tabular">
                      {quantity(line.quantity, material?.decimals ?? 2, material?.unitCode ?? '')}
                    </span>
                    <span>· {placeOf(line.activityId)}</span>
                    <span>
                      · {formatBusinessDate(line.requiredDate)} ({describeDeadline(line.daysUntil)})
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </CardContent>
    </Card>
  );
}

const REQUISITION_STATUS: Readonly<
  Record<MaterialRequisition['status'], { label: string; tone: 'ok' | 'info' | 'warn' }>
> = {
  // "Enviada" e não "atendida": o que saiu daqui foi a requisição; o que compras faz com
  // ela não é visível deste lado.
  SENT: { label: 'Enviada', tone: 'ok' },
  PENDING: { label: 'Aguardando envio', tone: 'info' },
  FAILED: { label: 'Não enviada', tone: 'warn' },
};

/**
 * O que já foi requisitado nesta obra — a ciência some com o item da lista de cima, e sem
 * este recibo sumiria sem rastro. É a única tela onde o motivo da falha aparece.
 */
function RequisitionHistory({ view }: { view: RequisitionHistoryView }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Já requisitado
          {view.pending > 0 && <Badge tone="info">{view.pending} aguardando envio</Badge>}
          {view.failed > 0 && <Badge tone="warn">{view.failed} não enviada(s)</Badge>}
        </CardTitle>
        <CardDescription>Enviada não quer dizer recebida.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {view.requisitions.map((requisition) => {
          const status = REQUISITION_STATUS[requisition.status];
          return (
            <article
              key={requisition.id}
              className="flex flex-col gap-1 border-b border-(--color-border) pb-3 last:border-0 last:pb-0"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{requisition.materialName}</span>
                <span className="text-xs text-(--color-content-muted)">
                  {requisition.materialCode}
                </span>
                <Badge tone={status.tone}>{status.label}</Badge>
              </div>
              <p className="text-sm text-(--color-content-muted)">
                <span className="tabular">
                  {quantity(requisition.quantity, requisition.decimals, requisition.unitCode)}
                </span>
                {' · '}
                {requisition.towerName === ''
                  ? requisition.floorName
                  : `${requisition.towerName} · ${requisition.floorName}`}
                {' — '}
                {requisition.disciplineName}
                {' · para '}
                {formatBusinessDate(requisition.requiredDate)}
              </p>
              <p className="text-xs text-(--color-content-muted)">
                Ciência de {requisition.acknowledgedByName} em{' '}
                {formatDateTime(requisition.acknowledgedAt)}
              </p>
              {requisition.status === 'FAILED' && requisition.lastError !== undefined && (
                // O motivo, e não "houve um erro": quem lê é quem pode consertar o nome
                // do pavimento que não casou.
                <p className="text-xs text-(--color-state-warn)">{requisition.lastError}</p>
              )}
            </article>
          );
        })}
      </CardContent>
    </Card>
  );
}
